// FECOA-1270 — connectivity + offline-cutoff-lock state for the participant
// offline queue. Deliberately dependency-free: api.js imports this from its
// response interceptor, and the queue imports api.js, so anything heavier here
// would create an import cycle.

const LAST_CONTACT_KEY = 'offlineLastServerContact';

// Any response from the backend (success or error — either proves the server
// is reachable) refreshes this. Called by the api.js interceptors.
export const markServerContact = () => {
  try {
    localStorage.setItem(LAST_CONTACT_KEY, String(Date.now()));
  } catch {
    // Storage full/unavailable — the lock fails open without a timestamp.
  }
};

export const getLastServerContact = () => {
  try {
    const raw = localStorage.getItem(LAST_CONTACT_KEY);
    const ms = raw ? Number(raw) : NaN;
    return Number.isFinite(ms) ? ms : null;
  } catch {
    return null;
  }
};

// Resolved study/sponsor cutoff, delivered inside the participant login
// payload's user object (see participant_login_view.py) and persisted to the
// `user` localStorage key by storeParticipantSession. null/0 = no cutoff.
export const getOfflineCutoffHours = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const hours = user?.effective_offline_cutoff_hours;
    return typeof hours === 'number' && hours > 0 ? hours : null;
  } catch {
    return null;
  }
};

// Device-side lock (FECOA-1270): once the device has gone longer than the
// cutoff without reaching the server, the app stops taking NEW offline
// answers. Already-queued answers still sync (server accept-and-flag). Fails
// open when either value is missing — the lock is a compliance nudge, not a
// data gate, and the server judges lateness by client_answered_at regardless.
export const isOfflineLockActive = (now = Date.now()) => {
  const cutoffHours = getOfflineCutoffHours();
  if (!cutoffHours) return false;
  const lastContact = getLastServerContact();
  if (!lastContact) return false;
  return now - lastContact > cutoffHours * 60 * 60 * 1000;
};

// A request that never produced an HTTP response: offline, DNS failure,
// timeout, CORS-blocked, or the server unreachable. Anything with a response
// is a real server answer and must NOT be treated as connectivity loss.
export const isNetworkError = (error) => {
  if (!error || error.isReadOnlyBlock) return false;
  if (error.response) return false;
  return Boolean(
    error.request ||
    error.code === 'ERR_NETWORK' ||
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT'
  );
};
